import React, { useEffect, useState } from 'react';
import { Building2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { apiCall } from '../../utils/api';
import { usePlatformSettings } from '../../contexts/PlatformSettingsContext';

/** Phase A — org workforce policies: shift visibility (Sec 6), weekly hour
 * limit (Sec 7/8) and distance unit default (Sec 2). Employer + agency. */
export default function WorkforcePoliciesCard() {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const { maxVisibilityRadiusKm: platformCap } = usePlatformSettings();

  useEffect(() => {
    apiCall('/api/org/workforce-settings').then((d) => setSettings(d.settings)).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const d = await apiCall('/api/org/workforce-settings', {
        method: 'PUT',
        body: JSON.stringify({
          shift_visibility: settings.shift_visibility,
          roster_first_hours: Number(settings.roster_first_hours) || 24,
          weekly_hour_limit: Number(settings.weekly_hour_limit) || 40,
          distance_unit_default: settings.distance_unit_default,
          max_visibility_radius_km: settings.max_visibility_radius_km === '' || settings.max_visibility_radius_km == null ? null : Number(settings.max_visibility_radius_km),
        }),
      });
      setSettings({ ...settings, ...d.settings });
      toast.success('Workforce policies saved');
    } catch (e) {
      toast.error(e?.message || 'Failed to save policies');
    } finally { setSaving(false); }
  };

  if (!settings) return null;

  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-5" data-testid="workforce-policies-card">
      <div className="flex items-center gap-2 mb-4">
        <Building2 className="w-5 h-5 text-indigo-600" />
        <h3 className="font-semibold text-gray-900">Workforce Policies</h3>
      </div>
      <div className="grid md:grid-cols-4 gap-4">
        <div>
          <label className="text-xs font-medium text-gray-500">Shift visibility</label>
          <select
            value={settings.shift_visibility}
            onChange={(e) => setSettings({ ...settings, shift_visibility: e.target.value })}
            className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
            data-testid="shift-visibility-select"
          >
            <option value="roster">My roster only (private)</option>
            <option value="hybrid">Roster first, then public</option>
            <option value="public">Public marketplace</option>
          </select>
          <p className="text-[11px] text-gray-400 mt-1">
            {settings.shift_visibility === 'roster'
              ? 'Only workers on your roster can see and apply to your shifts.'
              : settings.shift_visibility === 'hybrid'
                ? `Roster sees shifts first; unfilled shifts open to everyone after ${settings.roster_first_hours || 24}h.`
                : 'Every worker on the platform can see and apply to your shifts.'}
          </p>
          {settings.shift_visibility === 'hybrid' && (
            <div className="mt-2 flex items-center gap-2">
              <input
                type="number" min="1" max="336"
                value={settings.roster_first_hours || 24}
                onChange={(e) => setSettings({ ...settings, roster_first_hours: e.target.value })}
                className="w-20 px-2 py-1.5 border border-gray-200 rounded-lg text-sm"
                data-testid="roster-first-hours-input"
              />
              <span className="text-xs text-gray-500">hours roster-only before opening publicly</span>
            </div>
          )}
        </div>
        <div>
          <label className="text-xs font-medium text-gray-500">Weekly hour limit</label>
          <input
            type="number" min="1" max="168"
            value={settings.weekly_hour_limit}
            onChange={(e) => setSettings({ ...settings, weekly_hour_limit: e.target.value })}
            className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
            data-testid="weekly-hour-limit-input"
          />
          <p className="text-[11px] text-gray-400 mt-1">Assignments above this show an overtime warning (never blocked).</p>
        </div>
        <div>
          <label className="text-xs font-medium text-gray-500">Distance unit default</label>
          <select
            value={settings.distance_unit_default}
            onChange={(e) => setSettings({ ...settings, distance_unit_default: e.target.value })}
            className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
            data-testid="distance-unit-default-select"
          >
            <option value="km">Kilometers</option>
            <option value="mi">Miles</option>
          </select>
        </div>
        <div>
          <label className="text-xs font-medium text-gray-500">Max shift visibility radius (km)</label>
          <input
            type="number" min="1" max={platformCap || 1000}
            value={settings.max_visibility_radius_km ?? ''}
            placeholder={platformCap ? `Platform limit ${platformCap} km` : 'No limit'}
            onChange={(e) => setSettings({ ...settings, max_visibility_radius_km: e.target.value })}
            className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
            data-testid="max-visibility-radius-input"
          />
          <p className="text-[11px] text-gray-400 mt-1">
            Workers farther than this from a shift won't see it or be alerted.
            {platformCap ? ` Platform admin cap: ${platformCap} km.` : ' Blank = platform default.'}
          </p>
        </div>
      </div>
      <button
        onClick={save}
        disabled={saving}
        className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-40"
        data-testid="save-workforce-policies-btn"
      >
        {saving && <Loader2 className="w-4 h-4 animate-spin" />} Save Policies
      </button>
    </div>
  );
}
